import { useAuth } from "@clerk/nextjs";
import { zodResolver } from "@hookform/resolvers/zod";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import posthog from "posthog-js";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import OnboardingWizardStepWelcome from "./OnboardingWizardStepWelcome";
import {
	STEPS,
	type WizardFormValues,
	wizardFormSchema,
} from "./onboardingWizardForm";
import { type WizardMode, WizardModeProvider } from "./WizardModeContext";
import { useOnboarding } from "~/hooks";
import type { CptStudyPlanPreset } from "~/lib/cpt-study-plan-preset";

const STEP_MOTION = {
	initial: { opacity: 0, scale: 0.97, y: 6 },
	animate: {
		opacity: 1,
		scale: 1,
		y: 0,
		transition: { duration: 0.18, ease: "easeOut" },
	},
	exit: {
		opacity: 0,
		scale: 0.98,
		transition: { duration: 0.12, ease: "easeIn" },
	},
} as const;

interface OnboardingWizardProps {
	mode?: WizardMode;
	onExit?: () => void;
	// Selections filled in before the wizard opens (welcome modal shortcut).
	preset?: CptStudyPlanPreset | null;
}

const OnboardingWizard = ({
	mode = "live",
	onExit,
	preset = null,
}: OnboardingWizardProps) => {
	const { userId } = useAuth();
	const { step, learningPath, onboarding } = useOnboarding();

	const methods = useForm<WizardFormValues>({
		resolver: zodResolver(wizardFormSchema),
		defaultValues: {
			learningPath: preset ? "certification" : null,
			primaryProfessionId:
				preset?.primaryProfessionId ?? onboarding?.primaryProfessionId ?? "",
			secondaryProfessionId: onboarding?.secondaryProfessionId ?? null,
			cataMemberNumber: onboarding?.cataMemberNumber ?? null,
			selectedCertificationId:
				preset?.selectedCertificationId ??
				onboarding?.selectedCertificationId ??
				null,
			ceuGoals: {},
			interests: onboarding?.interests ?? [],
			phoneNumber: onboarding?.smsPhoneNumber ?? "",
			isOnboardingCompleted: undefined,
		},
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: fire once on mount
	useEffect(() => {
		if (mode === "live" && !userId) return;
		posthog.capture("onboarding:started", {
			mode,
			is_authed: !!userId,
			learning_path: learningPath,
			step,
			preset: preset ? "cpt_study_plan" : null,
		});
	}, []);

	useEffect(() => {
		if (mode === "live" && !userId) return;
		posthog.capture("onboarding:step_viewed", {
			mode,
			is_authed: !!userId,
			learning_path: learningPath,
			step,
			step_index: STEPS[step].index,
			preset: preset ? "cpt_study_plan" : null,
		});
	}, [step, mode, userId, learningPath, preset]);

	if (mode === "live" && !userId) return null;

	const { Component: Step } = STEPS[step];

	return (
		<WizardModeProvider mode={mode} onExit={onExit} preset={preset}>
			<FormProvider {...methods}>
				<AnimatePresence mode="wait" initial={false}>
					{step === "welcome" ? (
						<motion.div
							key="welcome"
							{...STEP_MOTION}
							className="h-full w-full"
						>
							<OnboardingWizardStepWelcome />
						</motion.div>
					) : (
						<motion.div
							key={step}
							{...STEP_MOTION}
							className="flex h-full min-h-0 w-full flex-1 flex-col items-center bg-white dark:bg-slate-800"
						>
							<div
								className={clsx(
									"flex h-full min-h-0 w-full max-w-2xl flex-1 flex-col phablet:px-6 px-4 pb-6 phablet:pb-10",
									mode === "deferred"
										? "phablet:pt-2 pt-2"
										: "phablet:pt-10 pt-6",
								)}
							>
								<Step />
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</FormProvider>
		</WizardModeProvider>
	);
};

export default OnboardingWizard;
